import { getAdventureBadgeIconSrc } from './adventureIcons';

interface BadgeLike {
  id: string;
  name: string;
  icon: string;
  order?: number;
}

interface SessionLike {
  completedSteps?: number;
  steps?: Array<{
    stepKey: string;
    status: string;
  }>;
}

const LEGACY_BADGE_STEP_ORDER = ['TEAM', 'EQUIPMENT', 'DANGER', 'ESCAPE'] as const;
const EXPANDED_BADGE_STEP_ORDER = ['TEAM', 'SCENE', 'EQUIPMENT', 'DANGER', 'ESCAPE'] as const;

const getBadgeStepOrder = (badgeCount: number) =>
  badgeCount >= EXPANDED_BADGE_STEP_ORDER.length ? EXPANDED_BADGE_STEP_ORDER : LEGACY_BADGE_STEP_ORDER;

const getCompletedCreativeStepKeys = (session?: SessionLike | null) => {
  if (!session?.steps?.length) {
    return [];
  }

  return session.steps
    .filter((step) => step.status === 'COMPLETED' && step.stepKey !== 'AVATAR')
    .map((step) => step.stepKey);
};

export interface BadgeSlotModel {
  id: string;
  name: string;
  icon: string;
  unlocked: boolean;
  hint: string;
}

export interface LockedAdventureCardModel {
  id: string;
  title: string;
  teaser: string;
  status: string;
}

const ADVENTURE_TASK_IMAGE_TITLES = new Set([
  '神奇的探险之旅',
  '那一刻，我长大了',
  '写读后感',
  '综合性学习',
  '他__了',
  '形形色色的人',
  '中国的世界文化遗产',
  '漫画的启示',
]);

export const getDueDateStatus = (dueDate: string | null) => {
  if (!dueDate) {
    return '长期任务';
  }

  const due = new Date(dueDate);
  const now = new Date();
  const diffMs = due.getTime() - now.getTime();
  const days = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

  return days === 0 ? '今天截止' : `还剩 ${days} 天`;
};

export const buildBadgeSlotModels = ({
  badges,
  earnedBadgeIds,
}: {
  badges: BadgeLike[];
  earnedBadgeIds: Set<string>;
}): BadgeSlotModel[] =>
  badges.map((badge) => ({
    ...badge,
    icon: getAdventureBadgeIconSrc(badge.name, badge.icon),
    unlocked: earnedBadgeIds.has(badge.id),
    hint: earnedBadgeIds.has(badge.id) ? '已点亮' : '尚未解锁',
  }));

export const getCompletedAdventureStepCount = (session?: SessionLike | null) => {
  if (!session) {
    return 0;
  }

  const completedCreativeSteps = getCompletedCreativeStepKeys(session);
  if (completedCreativeSteps.length > 0) {
    return Math.min(5, completedCreativeSteps.length);
  }

  const safeCount = session.completedSteps ?? 0;
  return Math.min(5, Math.max(0, safeCount - 1));
};

export const getEarnedAdventureBadgeNames = (session?: SessionLike | null, badges: BadgeLike[] = []) => {
  const orderedBadges = [...badges].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const badgeStepOrder = getBadgeStepOrder(orderedBadges.length);
  const completedCreativeSteps = getCompletedCreativeStepKeys(session);
  const hasUnifiedPreparationBadge = orderedBadges.some((badge) => badge.name === '整装待发');

  const names = new Set<string>();

  if (hasUnifiedPreparationBadge && session?.steps?.length) {
    const completedSteps = new Set(
      session.steps.filter((step) => step.status === 'COMPLETED').map((step) => step.stepKey)
    );

    if (completedSteps.has('TEAM') && completedSteps.has('SCENE') && completedSteps.has('EQUIPMENT')) {
      names.add('整装待发');
    }

    if (completedSteps.has('DANGER')) {
      names.add('巧设险局');
    }

    if (completedSteps.has('ESCAPE')) {
      names.add('逃生高手');
    }

    return names;
  }

  if (orderedBadges.length > 0 && completedCreativeSteps.length > 0) {
    badgeStepOrder.forEach((stepKey, index) => {
      if (completedCreativeSteps.includes(stepKey) && orderedBadges[index]) {
        names.add(orderedBadges[index].name);
      }
    });

    return names;
  }

  if (!session?.steps?.length) {
    return (session?.completedSteps ?? 0) >= 6
      ? new Set<string>(hasUnifiedPreparationBadge ? ['整装待发', '巧设险局', '逃生高手'] : ['慧眼识人', '精装上阵', '巧设险局', '逃生高手'])
      : new Set<string>();
  }

  session.steps.forEach((step) => {
    if (step.status !== 'COMPLETED') {
      return;
    }

    if (step.stepKey === 'TEAM') {
      names.add('慧眼识人');
    }

    if (step.stepKey === 'EQUIPMENT') {
      names.add('精装上阵');
    }

    if (step.stepKey === 'DANGER') {
      names.add('巧设险局');
    }

    if (step.stepKey === 'ESCAPE') {
      names.add('逃生高手');
    }
  });

  return names;
};

export const buildLockedAdventureCards = (): LockedAdventureCardModel[] => [
  { id: 'locked-2', title: '那一刻，我长大了', teaser: '成长瞬间的故事卡正在整理中。', status: '即将开启' },
  { id: 'locked-3', title: '写读后感', teaser: '阅读后的感受任务会在后续开放。', status: '敬请期待' },
  { id: 'locked-4', title: '综合性学习', teaser: '综合主题任务正在打磨活动结构。', status: '开发中' },
  { id: 'locked-5', title: '他__了', teaser: '人物情绪表达卡还在完善。', status: '开发中' },
  { id: 'locked-6', title: '形形色色的人', teaser: '人物观察与描写任务正在准备。', status: '敬请期待' },
  { id: 'locked-7', title: '中国的世界文化遗产', teaser: '文化遗产专题卡即将加入旅程。', status: '敬请期待' },
  { id: 'locked-8', title: '漫画的启示', teaser: '漫画理解与表达任务后续解锁。', status: '敬请期待' },
];

export const getAdventureTaskImageSrc = (title: string) =>
  ADVENTURE_TASK_IMAGE_TITLES.has(title) ? `/首页习作图片/${encodeURIComponent(title)}.png` : '';
